import { useRef, useState } from "react";
import { useToast } from "@/components/ui/Toast";
import { dayMonth } from "@/lib/brief";
import { pointer } from "@/lib/pointage";
import { cn } from "@/lib/utils";
import { parisDateOf } from "@shared/followup";
import type { Prospect } from "@shared/schema";

/** « ✓ Présent » / « ✕ Absent » : pointe le RDV en un clic (Absent pose un rappel demain). */
export function PointageButtons({
  prospectId,
  eventId,
  eventStart,
  onDone,
  className,
}: {
  prospectId: number;
  eventId: number;
  /** Début du RDV : sert au toast quand la fiche n'est pas touchée. */
  eventStart?: string;
  onDone: (prospect: Prospect, outcome: "attended" | "no_show") => void;
  className?: string;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  async function go(e: React.MouseEvent, kind: "present" | "no_show") {
    e.stopPropagation();
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const { prospect, touchedProspect } = await pointer(prospectId, eventId, kind);
      if (kind === "present") toast.success("Présent noté");
      else if (touchedProspect) {
        const day = dayMonth(prospect.nextFollowUpAt);
        toast.success(day ? `Absent noté : rappel ${day}` : "Absent noté");
      } else {
        const day = dayMonth(eventStart ? parisDateOf(eventStart) : null);
        toast.success(day ? `Absent noté (RDV du ${day})` : "Absent noté");
      }
      onDone(prospect, kind === "present" ? "attended" : "no_show");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Le pointage a échoué");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <span className={cn("inline-flex gap-1.5 sm:gap-2", className)}>
      <button
        type="button"
        disabled={busy}
        onClick={(e) => go(e, "present")}
        className="btn btn-success-outline h-11 sm:h-8 sm:px-3 sm:text-[13px]"
      >
        ✓ Présent
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={(e) => go(e, "no_show")}
        className="btn btn-muted h-11 sm:h-8 sm:px-3 sm:text-[13px]"
      >
        ✕ Absent
      </button>
    </span>
  );
}
